import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import {
  PasswordMode,
  TenantUser,
  UserRole,
  createUser,
  deleteUser,
  listUsers,
  setUserAccessRoles,
  setUserStatus,
} from "../../services/usersService";
import {
  AccessRole,
  listAccessRoles,
} from "../../services/accessRolesService";
import "./Users.css";

type Props = { user?: any; onSignOut?: () => void };
type CreatableRole = "INTEGRATOR" | "ADMIN" | "VIEWER";

function emailOf(user: any) {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function roleLabel(role: UserRole | null) {
  if (!role) return "Account";
  if (role === "OWNER") return "Owner";
  if (role === "INTEGRATOR") return "Integrator";
  if (role === "ADMIN") return "Admin";
  return "Viewer";
}

export default function UsersHierarchy({ user, onSignOut }: Props) {
  const [items, setItems] = useState<TenantUser[]>([]);
  const [accessRoles, setAccessRoles] = useState<AccessRole[]>([]);
  const [callerRole, setCallerRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editingUserId, setEditingUserId] = useState("");
  const [editRoleIds, setEditRoleIds] = useState<string[]>([]);
  const [draft, setDraft] = useState({
    email: "",
    displayName: "",
    role: "ADMIN" as CreatableRole,
    password: "",
    passwordMode: "TEMPORARY" as PasswordMode,
    accessRoleIds: [] as string[],
  });

  const roles = useMemo<CreatableRole[]>(() => {
    if (callerRole === "OWNER") return ["INTEGRATOR"];
    if (callerRole === "INTEGRATOR" || callerRole === "ADMIN") {
      return ["ADMIN", "VIEWER"];
    }
    return [];
  }, [callerRole]);

  const requiresAccessRoles =
    callerRole !== "OWNER" &&
    (draft.role === "ADMIN" || draft.role === "VIEWER");

  useEffect(() => {
    if (roles.length && !roles.includes(draft.role)) {
      setDraft((current) => ({
        ...current,
        role: roles[0],
        accessRoleIds: [],
      }));
    }
  }, [roles, draft.role]);

  const load = useCallback(async () => {
    setError("");
    try {
      const result = await listUsers();
      setItems(Array.isArray(result.items) ? result.items : []);
      setCallerRole(result.callerRole);
      if (result.callerRole === "INTEGRATOR" || result.callerRole === "ADMIN") {
        const access = await listAccessRoles();
        setAccessRoles(Array.isArray(access.items) ? access.items : []);
      } else {
        setAccessRoles([]);
      }
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Unable to load account hierarchy.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleDraftAccessRole(id: string) {
    setDraft((current) => ({
      ...current,
      accessRoleIds: current.accessRoleIds.includes(id)
        ? current.accessRoleIds.filter((roleId) => roleId !== id)
        : [...current.accessRoleIds, id],
    }));
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!roles.includes(draft.role)) return;
    if (requiresAccessRoles && draft.accessRoleIds.length === 0) {
      setError("Select at least one Access Role before creating this account.");
      return;
    }
    setBusy("create");
    setError("");
    setMessage("");
    try {
      await createUser({
        email: draft.email.trim(),
        displayName: draft.displayName.trim(),
        role: draft.role,
        password: draft.password,
        passwordMode: draft.passwordMode,
        accessRoleIds: requiresAccessRoles ? draft.accessRoleIds : undefined,
      });
      setDraft((current) => ({
        ...current,
        email: "",
        displayName: "",
        password: "",
        accessRoleIds: [],
      }));
      await load();
      setMessage(`${roleLabel(draft.role)} account created.`);
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Unable to create account.",
      );
    } finally {
      setBusy("");
    }
  }

  async function toggleStatus(target: TenantUser) {
    const next = target.status === "DISABLED" ? "ACTIVE" : "DISABLED";
    setBusy(`status:${target.userId}`);
    setError("");
    try {
      await setUserStatus(target.userId, next);
      await load();
      setMessage(`${target.displayName || target.email || "Account"} is now ${next.toLowerCase()}.`);
    } catch (statusError) {
      setError(
        statusError instanceof Error
          ? statusError.message
          : "Unable to update account status.",
      );
    } finally {
      setBusy("");
    }
  }

  async function saveUserAccessRoles(target: TenantUser) {
    if (editRoleIds.length === 0) {
      setError("Admin and Viewer accounts must have at least one Access Role.");
      return;
    }
    setBusy(`roles:${target.userId}`);
    setError("");
    setMessage("");
    try {
      await setUserAccessRoles(target.userId, editRoleIds);
      setEditingUserId("");
      setEditRoleIds([]);
      await load();
      setMessage(`Access Roles updated for ${target.displayName || target.email || "account"}.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update Access Roles.");
    } finally {
      setBusy("");
    }
  }

  async function remove(target: TenantUser) {
    if (
      window.confirm &&
      !window.confirm(
        `Delete ${target.displayName || target.email || "this account"}?`,
      )
    ) {
      return;
    }
    setBusy(`delete:${target.userId}`);
    setError("");
    try {
      await deleteUser(target.userId);
      await load();
      setMessage("Account deleted.");
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Unable to delete account.",
      );
    } finally {
      setBusy("");
    }
  }

  const visibleItems =
    callerRole === "OWNER"
      ? items.filter((item) => item.role === "INTEGRATOR")
      : items.filter((item) => item.role && item.role !== "OWNER");

  return (
    <div className="users-page">
      <TopBar onSignOut={onSignOut} email={emailOf(user)} />
      <main className="users-shell">
        <header className="users-hero">
          <div>
            <div className="users-eyebrow">Account hierarchy</div>
            <h1>Users</h1>
            <p>
              {callerRole === "OWNER"
                ? "Owner creates and manages Integrator accounts only. Admin and Viewer accounts are managed downstream."
                : callerRole === "INTEGRATOR"
                  ? "Integrator creates Admins or Viewers and assigns the Access Roles that define their camera visibility."
                  : callerRole === "ADMIN"
                    ? "Admin creates Admins or Viewers using only Access Roles assigned by the Integrator."
                    : "Account access is scoped by role."}
            </p>
          </div>
          <span className="users-role-badge">{roleLabel(callerRole)}</span>
        </header>

        {error && <div className="users-alert users-alert--error" role="alert">{error}</div>}
        {message && <div className="users-alert users-alert--success" role="status">{message}</div>}

        <section className="users-section">
          <div className="users-section__heading">
            <div>
              <div className="users-eyebrow">Create account</div>
              <h2>{callerRole === "OWNER" ? "Add Integrator" : "Add Admin or Viewer"}</h2>
              <p>
                {callerRole === "OWNER"
                  ? "New Integrators are automatically linked beneath the platform Owner."
                  : "Every new Admin or Viewer must receive at least one Access Role."}
              </p>
            </div>
          </div>

          <form className="users-create-form" onSubmit={create}>
            <label><span>Email</span><input type="email" required value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></label>
            <label><span>Display name</span><input required value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} /></label>
            <label><span>Role</span><select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as CreatableRole, accessRoleIds: [] })}>{roles.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}</select></label>
            <label><span>Password</span><input type="password" required minLength={8} value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} /></label>
            <label><span>Password type</span><select value={draft.passwordMode} onChange={(e) => setDraft({ ...draft, passwordMode: e.target.value as PasswordMode })}><option value="TEMPORARY">Temporary</option><option value="PERMANENT">Permanent</option></select></label>

            {requiresAccessRoles && (
              <fieldset className="users-access-roles-fieldset">
                <legend>Access Roles *</legend>
                {accessRoles.length === 0 ? (
                  <div className="users-access-roles-empty">
                    {callerRole === "INTEGRATOR"
                      ? "Create an Access Role first, then return here to create Admin or Viewer accounts."
                      : "Your Integrator must assign Access Roles before you can create downstream accounts."}
                  </div>
                ) : (
                  <div className="users-access-roles-grid">
                    {accessRoles.map((accessRole) => (
                      <label className="users-access-role-option" key={accessRole.accessRoleId}>
                        <input
                          type="checkbox"
                          checked={draft.accessRoleIds.includes(accessRole.accessRoleId)}
                          onChange={() => toggleDraftAccessRole(accessRole.accessRoleId)}
                        />
                        <span>
                          <strong>{accessRole.name}</strong>
                          <small>{accessRole.cameraCount} camera{accessRole.cameraCount === 1 ? "" : "s"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>
            )}

            <button
              className="users-primary"
              type="submit"
              disabled={
                busy === "create" ||
                roles.length === 0 ||
                (requiresAccessRoles && draft.accessRoleIds.length === 0)
              }
            >
              {busy === "create" ? "Creating…" : "Create account"}
            </button>
          </form>
        </section>

        <section className="users-section">
          <div className="users-section__heading">
            <div>
              <div className="users-eyebrow">Visible scope</div>
              <h2>{callerRole === "OWNER" ? "Integrator accounts" : "Accounts under you"}</h2>
              <p>
                {callerRole === "OWNER"
                  ? "Only Integrator accounts are visible here."
                  : "Sibling Admin branches remain private. Camera access is inherited from assigned Access Roles."}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="users-empty">Loading accounts…</div>
          ) : visibleItems.length === 0 ? (
            <div className="users-empty">No accounts have been created under this scope yet.</div>
          ) : (
            <div className="users-list">
              {visibleItems.map((target) => {
                const canEditAccess =
                  callerRole !== "OWNER" &&
                  (target.role === "ADMIN" || target.role === "VIEWER");
                const editing = editingUserId === target.userId;
                return (
                  <article className="users-card" key={target.userId}>
                    <div className="users-card__header">
                      <div><h3>{target.displayName || target.email || target.userId}</h3><span>{target.email}</span></div>
                      <span className="users-role-badge">{roleLabel(target.role)}</span>
                    </div>
                    <div className="users-card__meta">
                      <span>Status: <strong>{target.status || "UNKNOWN"}</strong></span>
                      <span>Access Roles: <strong>{target.accessRoleIds?.length || 0}</strong></span>
                    </div>
                    {target.accessRoleNames && target.accessRoleNames.length > 0 && (
                      <div className="users-access-role-tags">
                        {target.accessRoleNames.map((name) => <span key={name}>{name}</span>)}
                      </div>
                    )}

                    {editing && (
                      <div className="users-role-editor">
                        <strong>Camera Access Roles</strong>
                        <div className="users-access-roles-grid">
                          {accessRoles.map((accessRole) => (
                            <label className="users-access-role-option" key={accessRole.accessRoleId}>
                              <input
                                type="checkbox"
                                checked={editRoleIds.includes(accessRole.accessRoleId)}
                                onChange={() =>
                                  setEditRoleIds((current) =>
                                    current.includes(accessRole.accessRoleId)
                                      ? current.filter((id) => id !== accessRole.accessRoleId)
                                      : [...current, accessRole.accessRoleId],
                                  )
                                }
                              />
                              <span><strong>{accessRole.name}</strong><small>{accessRole.cameraCount} cameras</small></span>
                            </label>
                          ))}
                        </div>
                        <div className="users-role-editor__actions">
                          <button className="users-primary" type="button" disabled={Boolean(busy)} onClick={() => void saveUserAccessRoles(target)}>Save roles</button>
                          <button className="users-secondary" type="button" disabled={Boolean(busy)} onClick={() => { setEditingUserId(""); setEditRoleIds([]); }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    <div className="users-card__actions">
                      {canEditAccess && !editing && (
                        <button
                          className="users-secondary"
                          type="button"
                          disabled={Boolean(busy)}
                          onClick={() => {
                            setEditingUserId(target.userId);
                            setEditRoleIds(target.accessRoleIds || []);
                          }}
                        >
                          Access Roles
                        </button>
                      )}
                      <button className="users-secondary" type="button" disabled={Boolean(busy)} onClick={() => void toggleStatus(target)}>{target.status === "DISABLED" ? "Enable" : "Disable"}</button>
                      <button className="users-secondary" type="button" disabled={Boolean(busy)} onClick={() => void remove(target)}>Delete</button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
